import {
  IAccount,
  IAccountService,
  ProductType,
  ServiceType,
} from "@origin-digital/event-dispatcher";
import { mapServiceType } from "./serviceType";
import { isPresent } from "./utils";
import { BackendCrm } from "@origin-digital/platform-enums";

/**
 * Determines sort order of service types and whether to include each service type on the property.
 */
export const SERVICE_TYPE_CONFIGURATION: Readonly<
  Record<ServiceType, { rank: number; include: boolean }>
> = {
  ELECTRICITY: { rank: 1, include: true },
  NATURAL_GAS: { rank: 2, include: true },
  HOT_WATER: { rank: 3, include: true },
  BROADBAND: { rank: 4, include: true },
  LPG: { rank: 5, include: true },
  OHM: { rank: 6, include: false },
  VPP_BATTERY: { rank: 7, include: false },
  UNKNOWN: { rank: 8, include: false },
};

const isLpgAccount = (account: IAccount): boolean => {
  return account.backend === BackendCrm.LPG;
};

const isHotWaterAccount = (account: IAccount): boolean => {
  return (
    account.backend === BackendCrm.KRAKEN &&
    account.services.some(
      (service) => service.productType === ProductType.HOT_WATER
    )
  );
};

export const isClosedAccount = (account: IAccount): boolean =>
  account.status === "CLOSED" || account.status === "CANCELLED";

/**
 * Remove the accountId from the serviceId and trim any non-alphanumeric chars from the ends.
 */
const mapServiceId = (serviceId: string, accountId: string): string => {
  serviceId = serviceId.replace(accountId, "");
  serviceId = serviceId.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, "");
  return serviceId;
};

const mapLpgAccountServices = (account: IAccount): IAccountService[] => {
  const isClosed = isClosedAccount(account);
  const serviceId = "";
  const serviceType = ServiceType.LPG;
  return [
    {
      accountId: account.backendAccountId,
      serviceId,
      backendType: account.backend,
      serviceType,
      isClosed,
    },
  ];
};

const mapHotWaterAccountServices = (account: IAccount): IAccountService[] => {
  const isClosed = isClosedAccount(account);
  const serviceId = mapServiceId(
    account.services.find((service) => service.productType)?.backendServiceId ??
      "",
    account.backendAccountId
  );
  const serviceType = ServiceType.HOT_WATER;
  return [
    {
      accountId: account.backendAccountId,
      serviceId,
      backendType: account.backend,
      serviceType,
      isClosed,
    },
  ];
};

const mapAccountServicesDefault = (account: IAccount): IAccountService[] => {
  const isClosed = isClosedAccount(account);
  return account.services
    .map((service) => {
      const serviceType = mapServiceType(service.productType, account.backend);
      return SERVICE_TYPE_CONFIGURATION[serviceType].include
        ? {
            accountId: account.backendAccountId,
            serviceId: mapServiceId(
              service.backendServiceId,
              account.backendAccountId
            ),
            backendType: account.backend,
            serviceType: mapServiceType(service.productType, account.backend),
            isClosed,
          }
        : undefined;
    })
    .filter(isPresent);
};

const sortAccountServices = (a: IAccountService, b: IAccountService) => {
  if (
    SERVICE_TYPE_CONFIGURATION[a.serviceType].rank !==
    SERVICE_TYPE_CONFIGURATION[b.serviceType].rank
  ) {
    return (
      SERVICE_TYPE_CONFIGURATION[a.serviceType].rank -
      SERVICE_TYPE_CONFIGURATION[b.serviceType].rank
    );
  }
  if (a.isClosed !== b.isClosed) {
    return a.isClosed ? 1 : -1;
  }
  return a.accountId.localeCompare(b.accountId);
};

export const mapAccountServices = (accounts: IAccount[]): IAccountService[] => {
  const result: IAccountService[] = [];
  accounts.forEach((account) => {
    if (isLpgAccount(account)) {
      result.push(...mapLpgAccountServices(account));
    } else if (isHotWaterAccount(account)) {
      result.push(...mapHotWaterAccountServices(account));
    } else {
      result.push(...mapAccountServicesDefault(account));
    }
  });
  return result.sort(sortAccountServices);
};
